import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { Marquee } from '../components/Marquee';
import { Reveal } from '../components/Reveal';
import { RotatingSeal } from '../components/RotatingSeal';
import { SectionIntro } from '../components/SectionIntro';
import { VideoLightbox } from '../components/VideoLightbox';
import {
  ArcheSearchMock,
  DominanceMock,
  PublishMock,
  UtilizationMock,
} from '../components/ProductMocks';
import { useRegisterDialog } from '../components/register-dialog-context';
import {
  ArrowUpRightIcon,
  BarsIcon,
  BulbIcon,
  ClockIcon,
  CodeIcon,
  CoinsIcon,
  CubeIcon,
  GearIcon,
  HandCoinIcon,
  HeadIcon,
  KeyIcon,
  LinkIcon,
  PlayIcon,
  PulseIcon,
  RocketIcon,
  ShieldCheckIcon,
  WalletIcon,
} from '../components/icons';
import {
  ARCHITECTURE,
  BETA_PATH,
  CLOSING,
  DEVVX,
  HERO,
  INTERFACE_SECTION,
  PILLARS,
  PROBLEM,
  SOLUTION,
  VOICES_SECTION,
} from '../content';

/**
 * The Fias marketing home page.
 *
 * Pure presentation: no auth, no data fetching, no browser APIs during the
 * first render. That is a hard requirement, not a stylistic one — this
 * component is rendered to static HTML at build time so crawlers and social
 * scrapers get real markup (see client/scripts/prerender). Every animation on
 * the page is therefore CSS, and every one of them starts from a state that
 * already reads correctly if it never runs.
 */

/** Keyed by the `icon` field on each ARCHITECTURE entry. */
const ARCHITECTURE_ICONS: Record<string, ReactNode> = {
  key: <KeyIcon />,
  head: <HeadIcon />,
  code: <CodeIcon />,
  coins: <CoinsIcon />,
  wallet: <WalletIcon />,
  handCoin: <HandCoinIcon />,
  shield: <ShieldCheckIcon />,
  link: <LinkIcon />,
};

function Section({
  id,
  children,
  className = '',
}: {
  id?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section id={id} className={`mx-auto max-w-6xl px-6 py-20 md:py-28 ${className}`}>
      {children}
    </section>
  );
}

export function HomePage() {
  const { openRegisterDialog } = useRegisterDialog();
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <>
      {/* Hero -------------------------------------------------------- */}
      <section className="fias-hero px-6 pb-28 pt-36 md:pb-40 md:pt-44">
        {/*
          Decorative only — the headline beside it carries the meaning, so the
          video is hidden from assistive tech rather than described. `playsInline`
          is what stops iOS taking it fullscreen instead of playing it in place.
        */}
        <video
          className="fias-hero-media"
          src="/fias-hero-ribbon.mp4"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
          tabIndex={-1}
        />
        <div className="fias-hero-scrim" aria-hidden="true" />

        <div className="mx-auto max-w-6xl">
          <p className="fias-hero-pill">{HERO.eyebrow}</p>

          <div className="fias-hero-headline mt-8">
            <h1 className="max-w-[16ch] uppercase">{HERO.headline}</h1>
          </div>

          {/* One line from `md` up — the sentence needs ~584px at 18px, so the
              measure is widened past `max-w-lg` rather than left to wrap
              "digital economy" onto a line of its own. Narrower than `md` it
              falls back to two lines, which is the intended tight-fit shape. */}
          <p className="mt-8 max-w-lg font-sans text-base leading-relaxed tracking-[-0.02em] text-[color:var(--fias-hero-sub)] md:max-w-[40rem] md:text-lg">
            {HERO.subhead}
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/auth/login" className="fias-btn fias-btn-pill fias-btn-light">
              Login
            </Link>
            {/* Register is a button, not a link: the beta gate has to be
                explained before anyone lands on a signup form they may not be
                able to complete. */}
            <button
              type="button"
              onClick={openRegisterDialog}
              className="fias-btn fias-btn-pill fias-btn-veil"
            >
              Register
            </button>
          </div>
        </div>
      </section>

      {/* Pillars ----------------------------------------------------- */}
      {/* Two claims, each under the piece of product that backs it up. */}
      <Section className="!pt-0">
        <div className="grid gap-6 md:grid-cols-2">
          <PillarCard title={PILLARS[0].title} body={PILLARS[0].body} pairedTitle>
            <PublishMock />
          </PillarCard>
          <PillarCard title={PILLARS[1].title} body={PILLARS[1].body} pairedTitle>
            <ArcheSearchMock />
          </PillarCard>
        </div>
      </Section>

      {/* Problem / solution ------------------------------------------ */}
      {/* Deliberately a facing pair rather than two stacked sections —
          the solution only lands while the problem is still on screen. */}
      <Section id="solution">
        <div className="grid gap-6 md:grid-cols-2">
          <PillarCard label={PROBLEM.label} title={PROBLEM.title} body={PROBLEM.body}>
            <DominanceMock />
          </PillarCard>
          <PillarCard label={SOLUTION.label} title={SOLUTION.title} body={SOLUTION.body}>
            <UtilizationMock />
          </PillarCard>
        </div>
      </Section>

      {/* Interface --------------------------------------------------- */}
      <Section>
        <SectionIntro
          icon={<GearIcon />}
          eyebrow={INTERFACE_SECTION.eyebrow}
          heading={INTERFACE_SECTION.heading}
          body={INTERFACE_SECTION.body}
          align="center"
        />

        <hr className="fias-divider mt-16" />

        {/*
          Two rows travelling in opposite directions. Reading them is not the
          point — the field of capability is. Counter-motion is what stops it
          reading as a list you are meant to finish.
        */}
        <div className="space-y-4 py-12">
          <Marquee durationSeconds={44} className="-mx-6">
            {INTERFACE_SECTION.rowOne.map((tag) => (
              <span key={tag} className="fias-tag">
                {tag}
              </span>
            ))}
          </Marquee>
          <Marquee durationSeconds={44} reverse className="-mx-6">
            {INTERFACE_SECTION.rowTwo.map((tag) => (
              <span key={tag} className="fias-tag">
                {tag}
              </span>
            ))}
          </Marquee>
        </div>

        <hr className="fias-divider" />

        <Reveal className="mt-14 flex flex-col items-center gap-6 text-center sm:flex-row sm:justify-center sm:text-left">
          <h3 className="fias-display fias-display-sm max-w-md text-2xl font-semibold leading-[1.3]">
            {INTERFACE_SECTION.closer}
          </h3>
          <Link to={BETA_PATH} className="fias-btn fias-btn-veil">
            Try Fias Now
          </Link>
        </Reveal>
      </Section>

      {/* DevvX ------------------------------------------------------- */}
      <Section>
        {/* The heading is long enough that a narrower column breaks it onto a
            third line and it stops reading as one statement. */}
        <div className="grid items-center gap-14 lg:grid-cols-[1.25fr_0.75fr]">
          <div>
            <SectionIntro icon={<RocketIcon />} eyebrow={DEVVX.eyebrow} heading={DEVVX.heading} />

            <div className="mt-10 space-y-4">
              {DEVVX.features.map((feature, index) => (
                <Reveal key={feature.title} className="fias-panel max-w-sm p-6" delay={index * 110}>
                  <h3 className="fias-display fias-display-sm text-2xl font-medium">
                    {feature.title}
                  </h3>
                  <p className="mt-2 flex items-center gap-2 font-sans text-sm text-[color:var(--fias-muted)]">
                    <span className="h-4 w-4 shrink-0">
                      {index === 0 ? <ClockIcon /> : <BarsIcon />}
                    </span>
                    {feature.detail}
                  </p>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Tipped a few degrees so it reads as an object photographed on the
              page rather than a figure placed in a grid slot. */}
          <div className="relative flex justify-center lg:justify-end">
            <Reveal
              className="w-full max-w-sm overflow-hidden rounded-2xl"
              distance={40}
              style={{
                transform: 'rotate(-3deg)',
                boxShadow: '0 40px 80px -40px rgba(0,0,0,0.9)',
              }}
            >
              {/* A poster that opens the player, not a video that loads with
                  the page. The button carries the whole poster so the click
                  target is the image itself, the way it reads.

                  With no CDN configured there is nothing to play, so the poster
                  renders as a plain image — a play button that leads nowhere is
                  worse than no play button. */}
              {DEVVX.video.src ? (
                <button
                  type="button"
                  onClick={() => setVideoOpen(true)}
                  className="fias-video-poster"
                  aria-label={`Play the ${DEVVX.video.title} video, ${DEVVX.video.durationLabel}`}
                >
                  <DevvXPoster />
                  <span className="fias-video-play" aria-hidden="true">
                    <PlayIcon />
                  </span>
                </button>
              ) : (
                <DevvXPoster />
              )}
            </Reveal>
          </div>
        </div>

        <VideoLightbox
          open={videoOpen}
          onClose={() => setVideoOpen(false)}
          src={DEVVX.video.src}
          poster={DEVVX.video.poster}
          title={DEVVX.video.title}
        />
      </Section>

      {/* Voices ------------------------------------------------------ */}
      <Section>
        <SectionIntro
          icon={<PulseIcon />}
          eyebrow={VOICES_SECTION.eyebrow}
          heading={VOICES_SECTION.heading}
          aside={
            <Link to={BETA_PATH} className="fias-btn fias-btn-veil">
              Try Fias Now
            </Link>
          }
        />

        {/* Tipped in alternating directions — these are things people handed
            us, not three equal columns of marketing copy. */}
        <div className="mt-16 grid gap-6 md:grid-cols-3">
          {VOICES_SECTION.quotes.map((quote, index) => (
            <Reveal
              as="blockquote"
              key={quote}
              className="fias-quote fias-panel p-7"
              delay={index * 120}
            >
              <p className="font-sans text-sm leading-relaxed tracking-[-0.02em] text-[color:var(--fias-muted)]">
                “{quote}”
              </p>
            </Reveal>
          ))}
        </div>

        <Reveal className="fias-panel mt-20 px-8 py-14" distance={32}>
          <p className="fias-display fias-display-sm mx-auto max-w-3xl text-center text-[clamp(1.375rem,2.6vw,2rem)] font-medium leading-[1.35]">
            {VOICES_SECTION.answer}
          </p>
        </Reveal>
      </Section>

      {/* Architecture ------------------------------------------------ */}
      <Section>
        <SectionIntro
          icon={<CubeIcon />}
          eyebrow={ARCHITECTURE.eyebrow}
          heading={ARCHITECTURE.heading}
          body={ARCHITECTURE.body}
        />

        {/* Eight features across two counter-scrolling rows. Card order within
            a row is presentation, not ranking. */}
        <div className="mt-16 space-y-6">
          <Marquee durationSeconds={64} gapClassName="gap-6" className="-mx-6">
            {ARCHITECTURE.rowOne.map((item) => (
              <ArchitectureCard
                key={item.title}
                icon={item.icon}
                title={item.title}
                body={item.body}
              />
            ))}
          </Marquee>
          <Marquee durationSeconds={64} reverse gapClassName="gap-6" className="-mx-6">
            {ARCHITECTURE.rowTwo.map((item) => (
              <ArchitectureCard
                key={item.title}
                icon={item.icon}
                title={item.title}
                body={item.body}
              />
            ))}
          </Marquee>
        </div>
      </Section>

      {/* Closing ----------------------------------------------------- */}
      <section className="fias-closing">
        {/* The hero's ribbon returns, mirrored and dimmed — the same light seen
            from the far end of the page. */}
        <video
          className="fias-closing-media"
          src="/fias-hero-ribbon.mp4"
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          aria-hidden="true"
          tabIndex={-1}
        />

        <div className="mx-auto grid max-w-6xl items-center gap-12 px-6 py-24 md:py-32 lg:grid-cols-[1fr_0.8fr]">
          <div>
            <Reveal>
              <p className="fias-eyebrow">
                <BulbIcon />
                <span>
                  {CLOSING.eyebrow.lead}
                  <span className="fias-eyebrow-trail">{CLOSING.eyebrow.trail}</span>
                </span>
              </p>
            </Reveal>

            <Reveal delay={90}>
              <h2 className="fias-display mt-4 max-w-xl text-[clamp(2rem,4.2vw,2.75rem)] font-semibold leading-[1.2]">
                {CLOSING.heading}
              </h2>
            </Reveal>

            <Reveal delay={180}>
              <p className="mt-5 max-w-lg font-sans text-base leading-relaxed tracking-[-0.02em] text-[color:var(--fias-muted)]">
                {CLOSING.body}
              </p>
            </Reveal>

            <Reveal delay={270} className="mt-9">
              <Link to={BETA_PATH} className="fias-btn fias-btn-pill fias-btn-primary">
                Try Fias
              </Link>
            </Reveal>
          </div>

          <div className="flex justify-center lg:justify-end">
            {/* Positioned against the image rather than the column so the seal
                keeps overlapping the corner at every width. */}
            <div className="relative w-full max-w-[22rem]">
              <Reveal
                className="overflow-hidden rounded-2xl bg-white"
                distance={40}
                style={{ transform: 'rotate(2.5deg)' }}
              >
                <img
                  src="/fias-phone-silhouette.png"
                  alt="Two hands holding a lit phone"
                  width={366}
                  height={366}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </Reveal>

              {/* Overlaps the top-left corner, the way a stamp sits across the
                  thing it certifies. */}
              <RotatingSeal
                text={CLOSING.seal}
                className="pointer-events-none absolute -left-16 -top-12 hidden h-48 w-48 text-[color:var(--fias-muted)] sm:block"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

/** The DevvX still. Shared by the playable and non-playable branches. */
function DevvXPoster() {
  return (
    <img
      src={DEVVX.video.poster}
      alt={DEVVX.video.posterAlt}
      width={366}
      height={366}
      loading="lazy"
      className="h-full w-full object-cover"
    />
  );
}

/**
 * A claim with the piece of product that backs it up sitting above it.
 * `label` renders on its own line above the title where a section has one
 * ("The problem:"), matching how the pair reads as a single sentence.
 */
function PillarCard({
  label,
  title,
  body,
  pairedTitle = false,
  children,
}: {
  label?: string;
  title: string;
  body: string;
  /**
   * Set on cards that sit side by side as a matched pair. Caps the title
   * measure below what either title needs on one line and balances the wrap,
   * so both sit on two lines and the body copy starts at the same height.
   * Left off elsewhere: the problem/solution titles differ too much in length
   * for a shared cap to do anything but add lines.
   */
  pairedTitle?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="fias-panel overflow-hidden p-6 md:p-7">
      {children}

      <Reveal delay={120}>
        <h3
          className={`fias-display fias-display-sm mt-8 text-2xl font-semibold leading-[1.3] ${
            pairedTitle ? 'text-balance md:max-w-[26rem]' : ''
          }`}
        >
          {label ? (
            <>
              <span className="block uppercase">{label}</span>
              {title}
            </>
          ) : (
            title
          )}
        </h3>
      </Reveal>

      <Reveal delay={210}>
        <p className="mt-4 font-sans text-base leading-relaxed tracking-[-0.02em] text-[color:var(--fias-muted)]">
          {body}
        </p>
      </Reveal>
    </div>
  );
}

/** One architecture feature. Fixed width so the marquee track is predictable. */
function ArchitectureCard({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <div className="fias-panel relative w-[19rem] shrink-0 p-7 sm:w-[23rem]">
      <span
        aria-hidden="true"
        className="absolute right-6 top-6 h-4 w-4 text-[color:var(--fias-dim)]"
      >
        <ArrowUpRightIcon />
      </span>

      <span
        aria-hidden="true"
        className="fias-mock-row flex h-12 w-12 items-center justify-center text-[color:var(--fias-muted)]"
      >
        <span className="h-5 w-5">{ARCHITECTURE_ICONS[icon]}</span>
      </span>

      <h3 className="fias-display fias-display-sm mt-6 max-w-[85%] text-xl font-semibold">
        {title}
      </h3>
      <p className="mt-3 font-sans text-sm leading-relaxed tracking-[-0.02em] text-[color:var(--fias-muted)]">
        {body}
      </p>
    </div>
  );
}
